import { useEffect } from 'react';

/**
 * 根据表体纵向滚动位置切换 class，配合 global.css 显示上下内阴影。
 *
 * @param {React.RefObject<HTMLElement | null>} scrollContainerRef
 * @param {object} [options]
 * @param {boolean} [options.enabled]
 * @param {number} [options.threshold]
 * @param {unknown} [options.watchValue]
 */
export function useVerticalScrollShadow(scrollContainerRef, options = {}) {
    const enabled = options.enabled ?? true;
    const threshold = options.threshold ?? 2;
    const watchValue = options.watchValue;

    useEffect(() => {
        if (!enabled) return;
        void watchValue;

        const scrollEl = scrollContainerRef.current;
        if (!scrollEl) return;

        const update = () => {
            const maxScrollTop = Math.max(0, scrollEl.scrollHeight - scrollEl.clientHeight);
            const hasOverflow = maxScrollTop > threshold;
            const atTop = scrollEl.scrollTop <= threshold;
            const atBottom = maxScrollTop - scrollEl.scrollTop <= threshold;

            scrollEl.classList.toggle('custom-table-vertical-shadow', hasOverflow);
            scrollEl.classList.toggle('custom-table-vertical-shadow-top', hasOverflow && !atTop);
            scrollEl.classList.toggle('custom-table-vertical-shadow-bottom', hasOverflow && !atBottom);
        };

        const mutationObserver = new MutationObserver(update);
        mutationObserver.observe(scrollEl, { childList: true, subtree: true });

        const resizeObserver = new ResizeObserver(update);
        resizeObserver.observe(scrollEl);

        scrollEl.addEventListener('scroll', update, { passive: true });
        update();
        const rafId = requestAnimationFrame(update);

        return () => {
            cancelAnimationFrame(rafId);
            scrollEl.removeEventListener('scroll', update);
            mutationObserver.disconnect();
            resizeObserver.disconnect();
            scrollEl.classList.remove('custom-table-vertical-shadow', 'custom-table-vertical-shadow-top', 'custom-table-vertical-shadow-bottom');
        };
    }, [enabled, threshold, scrollContainerRef, watchValue]);
}
